import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, post, upload } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageLoader } from "@/components/common";
import { Clock, ChevronLeft, ChevronRight, Flag, Eraser, Send, X } from "lucide-react";

const SC = {
  not_visited: "bg-slate-200 text-slate-700", not_answered: "bg-red-500 text-white",
  answered: "bg-green-600 text-white", marked: "bg-violet-600 text-white",
  answered_marked: "bg-violet-600 text-white ring-2 ring-green-400 ring-offset-1",
};

export default function ExamInterface() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const { lang, setLang } = useLang();
  const [data, setData] = useState(null);
  const [tmap, setTmap] = useState({});
  const [answers, setAnswers] = useState({});
  const [states, setStates] = useState({});
  const [secIdx, setSecIdx] = useState(0);
  const [idx, setIdx] = useState(0);
  const [submittedSecs, setSubmittedSecs] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [secRemaining, setSecRemaining] = useState(0);
  const [stage, setStage] = useState("loading"); // pattern | instr1 | instr2 | exam
  const [uiMode, setUiMode] = useState("railways"); // eduquity | tcs | railways
  const [agree, setAgree] = useState(false);
  const [reviewSec, setReviewSec] = useState(false);
  const [reviewTest, setReviewTest] = useState(false);
  const [subjPhase, setSubjPhase] = useState(false);
  const [subjIdx, setSubjIdx] = useState(0);
  const [copyStage, setCopyStage] = useState(false);
  const [copyFile, setCopyFile] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [subjSec, setSubjSec] = useState(0);
  const [copySec, setCopySec] = useState(900);
  const submittedRef = useRef(false);
  const [pauseAsk, setPauseAsk] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  const resumeSecRef = useRef(null);
  useEffect(() => {
    const on = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => toast.error("Fullscreen not supported on this device"));
  };
  const zoomBy = (d) => setZoom((z) => Math.min(1.6, Math.max(0.8, Math.round((z + d) * 10) / 10)));
  const [camOn, setCamOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const toggleCamera = async () => {
    if (camOn) { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setCamOn(false); return; }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: true });
      setCamOn(true);
    } catch { toast.error("Camera access denied or unavailable"); }
  };
  useEffect(() => { if (camOn && videoRef.current) videoRef.current.srcObject = streamRef.current; }, [camOn]);
  useEffect(() => () => streamRef.current?.getTracks().forEach((t) => t.stop()), []);

  useEffect(() => {
    get(`/attempts/${attemptId}/play`).then((d) => {
      setData(d);
      setAnswers(d.attempt.answers || {});
      setStates(d.attempt.states || {});
      setRemaining(d.remaining_sec);
      const p = d.test.pattern || "railways";
      const us = d.resumed_from_pause ? d.attempt.ui_state || {} : null;
      if (us) {
        setUiMode(us.ui_mode || (p === "ssc" ? "eduquity" : "railways"));
        setSecIdx(us.sec_idx || 0); setIdx(us.idx || 0); setSubmittedSecs(us.submitted_secs || []);
        if (us.sec_remaining != null) resumeSecRef.current = us.sec_remaining;
        setStage("exam"); toast.success("Test resumed");
      }
      else if (p === "ssc") { setStage("pattern"); setUiMode("eduquity"); }
      else { setStage("instr1"); setUiMode("railways"); }
    }).catch(() => { toast.error("Could not load exam"); nav("/app/tests"); });
  }, [attemptId, nav]);

  const doSubmit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    try {
      await post(`/attempts/${attemptId}/submit`);
      toast.success(auto ? "Time up! Test submitted." : "Test submitted!");
      nav(`/results/${attemptId}`);
    } catch { toast.error("Submit failed"); submittedRef.current = false; }
  }, [attemptId, nav]);

  const sectional = data?.test?.pattern === "ssc";
  const groups = (data?.test?.sections_meta || []).map((m) => ({
    ...m, indices: (data?.attempt?.question_order || []).map((o, i) => ({ o, i }))
      .filter((x) => x.o.section === m.name).map((x) => x.i),
  }));

  // start/refresh section timer when entering a section
  useEffect(() => {
    if (stage !== "exam" || !sectional || subjPhase) return;
    const g = groups[secIdx];
    if (resumeSecRef.current != null) { setSecRemaining(resumeSecRef.current); resumeSecRef.current = null; }
    else if (g) setSecRemaining((g.duration_min || 0) * 60);
  }, [stage, secIdx, sectional, subjPhase]); // eslint-disable-line

  // total timer (railways)
  useEffect(() => {
    if (stage !== "exam" || sectional) return;
    const t = setInterval(() => setRemaining((r) => { if (r <= 1) { clearInterval(t); doSubmit(true); return 0; } return r - 1; }), 1000);
    return () => clearInterval(t);
  }, [stage, sectional, doSubmit]);

  // section timer (ssc)
  useEffect(() => {
    if (stage !== "exam" || !sectional || subjPhase) return;
    const t = setInterval(() => setSecRemaining((r) => { if (r <= 1) { clearInterval(t); submitSection(true); return 0; } return r - 1; }), 1000);
    return () => clearInterval(t);
  }, [stage, sectional, secIdx, subjPhase]); // eslint-disable-line

  // live translation
  useEffect(() => {
    if (!data || lang !== "hi" || subjPhase || stage !== "exam") return;
    const g = groups[secIdx]; if (!g || !g.indices.length) return;
    const oi = g.indices[idx]; if (oi == null) return;
    const qId = data.attempt.question_order[oi].question_id;
    if (!tmap[qId]) post("/i18n/translate", { entity_type: "question", entity_id: qId, target: "hi" }).then((r) => setTmap((m) => ({ ...m, [qId]: r }))).catch(() => {});
  }, [data, idx, secIdx, lang, subjPhase, stage]); // eslint-disable-line

  const submitCopy = async (auto = false) => {
    setCopyBusy(true);
    try {
      let fileId = null;
      if (copyFile) { const r = await upload(copyFile); fileId = r.id; }
      if (fileId) await post("/copy-submissions", { attempt_id: attemptId, file_id: fileId });
      await doSubmit(auto);
    } catch { toast.error("Copy upload failed"); setCopyBusy(false); }
  };
  useEffect(() => { if (subjPhase && !copyStage) setSubjSec((data?.test?.subjective_duration_min || 1) * 60); }, [subjPhase]); // eslint-disable-line
  useEffect(() => {
    if (!subjPhase || copyStage) return;
    const t = setInterval(() => setSubjSec((s) => { if (s <= 1) { clearInterval(t); setCopyStage(true); return 0; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [subjPhase, copyStage]);
  useEffect(() => {
    if (!copyStage) return;
    const t = setInterval(() => setCopySec((s) => { if (s <= 1) { clearInterval(t); submitCopy(true); return 0; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [copyStage]); // eslint-disable-line

  if (!data) return <PageLoader label="Loading exam..." />;

  const order = data.attempt.question_order;
  const subjPages = data.test.subjective_pages || [];
  const hasSubj = data.test.has_subjective && subjPages.length > 0;
  const finishTest = () => {
    setReviewTest(false);
    if (hasSubj && !subjPhase) { setSubjPhase(true); setSubjIdx(0); }
    else doSubmit(false);
  };
  const curGroup = groups[secIdx] || { indices: [], name: "" };
  const gIndex = curGroup.indices[idx];
  const cur = gIndex != null ? order[gIndex] : null;
  const qid = cur?.question_id;
  const base = qid ? data.questions[qid] : null;
  const q = lang === "hi" && qid && tmap[qid] ? { ...base, ...tmap[qid] } : base;
  const selected = (qid && answers[qid]) || [];

  const persist = async (qId, ans, st) => {
    setAnswers((a) => ({ ...a, [qId]: ans }));
    setStates((s) => ({ ...s, [qId]: st }));
    await post(`/attempts/${attemptId}/answer`, { question_id: qId, answer: ans, state: st }).catch(() => {});
  };
  const pick = (key) => {
    const ans = selected.includes(key) ? selected.filter((k) => k !== key) : (q.question_type === "multiple" ? [...selected, key] : [key]);
    const marked = (states[qid] || "").includes("marked");
    persist(qid, ans, ans.length ? (marked ? "answered_marked" : "answered") : (marked ? "marked" : "not_answered"));
  };
  const gotoQ = (i) => { setIdx(i); const o = curGroup.indices[i]; const nq = order[o].question_id; if (!states[nq]) setStates((s) => ({ ...s, [nq]: "not_answered" })); };
  const saveNext = () => { if (idx < curGroup.indices.length - 1) gotoQ(idx + 1); else gotoQ(0); };
  const clearResp = () => persist(qid, [], "not_answered");
  const markNext = () => { const ans = answers[qid] || []; persist(qid, ans, ans.length ? "answered_marked" : "marked"); saveNext(); };
  const switchSection = (si) => { if (sectional && si !== secIdx) return; setSecIdx(si); setIdx(0); };

  const submitSection = (auto = false) => {
    const name = groups[secIdx]?.name;
    const done = [...new Set([...submittedSecs, name])];
    setSubmittedSecs(done);
    setReviewSec(false);
    if (done.length >= groups.length) {
      if (hasSubj) { setSubjPhase(true); setSubjIdx(0); }
      else doSubmit(auto);
    } else {
      const next = groups.findIndex((g) => !done.includes(g.name));
      if (next >= 0) { setSecIdx(next); setIdx(0); }
    }
  };

  const pauseTest = async () => {
    try {
      await post(`/attempts/${attemptId}/pause`, { ui_state: { ui_mode: uiMode, sec_idx: secIdx, idx, submitted_secs: submittedSecs, sec_remaining: sectional ? secRemaining : null } });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (document.fullscreenElement) document.exitFullscreen?.();
      toast.success("Test paused. Start the same test again to resume.");
      nav("/app");
    } catch { toast.error("Could not pause test"); setPauseAsk(false); }
  };
  const pauseDlg = pauseAsk && <PauseDialog onNo={() => setPauseAsk(false)} onYes={pauseTest} />;
  const fsLabel = isFs ? "Exit Fullscreen" : "Show Fullscreen";
  const ZoomBtns = ({ cls }) => <>
    <button onClick={() => zoomBy(0.1)} data-testid="zoom-in-btn" className={cls}>Zoom (+)</button>
    <button onClick={() => zoomBy(-0.1)} data-testid="zoom-out-btn" className={cls}>Zoom (-)</button>
  </>;
  const stateOf = (qId) => states[qId] || "not_visited";
  const secStats = (g) => {
    const s = { count: g.indices.length, answered: 0, not_answered: 0, marked: 0, answered_marked: 0, not_visited: 0, visited: 0 };
    g.indices.forEach((oi) => { const st = stateOf(order[oi].question_id); s[st] = (s[st] || 0) + 1; if (st !== "not_visited") s.visited += 1; });
    return s;
  };
  const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const totalAnswered = Object.values(states).filter((v) => v === "answered" || v === "answered_marked").length;

  /* ---------- pre-exam stages ---------- */
  if (stage === "pattern") {
    return <Modal title="Test Interface">
      <p className="text-slate-600 mb-5">Which Test Interface do you want to continue?</p>
      {[["eduquity", "New Pattern (Eduquity)"], ["tcs", "Old Pattern (TCS)"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 py-2 cursor-pointer" data-testid={`pattern-${k}`}>
          <input type="radio" name="pat" checked={uiMode === k} onChange={() => setUiMode(k)} /> <span className="font-medium text-slate-800">{l}</span>
        </label>
      ))}
      <button onClick={() => setStage("instr1")} data-testid="pattern-confirm-btn" className="mt-4 bg-indigo-700 text-white font-bold px-6 py-2 rounded-lg">Confirm</button>
    </Modal>;
  }
  if (stage === "instr1" || stage === "instr2") {
    return <InstructionPage stage={stage} test={data.test} lang={lang} setLang={setLang} agree={agree} setAgree={setAgree}
      onBack={() => { if (stage === "instr2") setStage("instr1"); else nav("/app/tests"); }}
      onNext={() => setStage("instr2")} onStart={() => setStage("exam")} />;
  }

  /* ---------- copy upload stage ---------- */
  if (copyStage) return (
    <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col items-center justify-center p-6" data-testid="copy-upload-stage">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-lg w-full text-center">
        <p className="text-sm font-bold text-red-600 mb-2" data-testid="copy-timer">Time left to upload: {fmt(copySec)}</p>
        <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-3">Please upload your written copy</h2>
        <p className="text-slate-500 text-sm mb-5">Upload a photo or PDF of your handwritten answer sheet. You have 15 minutes to submit.</p>
        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 cursor-pointer mb-4 text-slate-500" data-testid="copy-file-label">
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setCopyFile(e.target.files[0])} />
          {copyFile ? copyFile.name : "Click to choose your copy file"}
        </label>
        <button onClick={() => submitCopy(false)} disabled={copyBusy || !copyFile} data-testid="copy-submit-btn" className="w-full bg-teal-700 hover:bg-teal-800 text-white font-bold py-2.5 rounded-lg disabled:opacity-40">{copyBusy ? "Submitting..." : "Submit Copy"}</button>
      </div>
    </div>
  );

  /* ---------- subjective phase ---------- */
  if (subjPhase) {
    return <div className="fixed inset-0 z-50 bg-slate-100 flex flex-col">
      <Topbar title={data.test.title} timer={fmt(subjSec)} />
      <div className="flex-1 overflow-y-auto p-6"><div className="max-w-4xl mx-auto h-full flex flex-col">
        <p className="font-display font-bold text-slate-900 mb-3">Section 2 · Subjective — {subjPages[subjIdx]?.title} <span className="text-slate-400 font-normal">· Page {subjIdx + 1} of {subjPages.length}</span></p>
        {subjPages[subjIdx]?.pdf_url
          ? <iframe title="subjective" src={`${process.env.REACT_APP_BACKEND_URL}${subjPages[subjIdx].pdf_url}#toolbar=0`} className="w-full flex-1 min-h-[70vh] bg-white rounded-xl border border-slate-200" data-testid="subjective-page-pdf" />
          : <div className="bg-white rounded-xl border border-slate-200 p-5 whitespace-pre-wrap text-slate-800" data-testid="subjective-page-text">{subjPages[subjIdx]?.text || "(No content)"}</div>}
      </div></div>
      <div className="bg-white border-t p-3 flex items-center gap-2">
        <button onClick={() => setSubjIdx(Math.max(0, subjIdx - 1))} disabled={subjIdx === 0} data-testid="subj-prev-btn" className="px-4 py-2 rounded-lg border text-sm font-semibold disabled:opacity-40">Prev Page</button>
        <div className="ml-auto flex gap-2">
          {subjIdx < subjPages.length - 1 && <button onClick={() => setSubjIdx(subjIdx + 1)} data-testid="subj-next-btn" className="px-5 py-2 rounded-lg bg-indigo-800 text-white text-sm font-bold">Next Page</button>}
          <button onClick={() => setCopyStage(true)} data-testid="subj-submit-btn" className="px-5 py-2 rounded-lg bg-teal-700 text-white text-sm font-bold">Proceed to Upload Copy</button>
        </div>
      </div>
    </div>;
  }

  /* ---------- exam ---------- */
  const eduquity = uiMode === "eduquity";
  const mmss = `${String(Math.floor(secRemaining / 60)).padStart(2, "0")} : ${String(secRemaining % 60).padStart(2, "0")}`;
  const EDU_SC = { not_visited: "bg-blue-700 text-white", not_answered: "bg-orange-400 text-white", answered: "bg-green-600 text-white", marked: "bg-red-600 text-white", answered_marked: "bg-red-600 text-white" };
  const aStats = secStats(curGroup);
  if (eduquity) return (
    <div className="fixed inset-0 z-40 bg-white flex flex-col text-slate-800" data-testid="eduquity-exam">
      {/* HEADER */}
      <header className="border-b border-slate-200 px-3 py-1.5 flex items-start gap-3 shrink-0">
        <div className="flex items-start gap-3">
          <div className="text-[10px] leading-tight">
            <div className="flex items-center gap-1 font-extrabold text-indigo-900"><span className="h-5 w-5 rounded-full bg-indigo-800 text-white flex items-center justify-center text-[9px]">R</span> Rank Test</div>
            <p className="text-slate-500 mt-0.5 max-w-[120px]">{data.test.title}</p>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex gap-1">
              <ZoomBtns cls="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded" />
            </div>
            <button onClick={toggleFs} data-testid="fullscreen-btn" className="border border-slate-400 text-xs font-bold px-2 py-0.5 rounded">{fsLabel}</button>
          </div>
        </div>
        <div className="flex-1 text-center pt-1">
          <p className="font-bold text-slate-900">{data.test.title}</p>
          <p className="text-xs text-slate-600">Roll No : 8083086*** [{(data.attempt.id || "").slice(0, 5)}]</p>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <span className="text-slate-400 text-lg">☀</span>
          <button onClick={() => setPauseAsk(true)} data-testid="exam-pause-btn" className="border border-slate-300 rounded px-2 py-1 text-sm font-bold">|| Pause</button>
          <div className="text-center">
            <p className="text-[10px] font-bold text-slate-500">Time Left</p>
            <p className="bg-yellow-100 text-red-600 font-black px-2 rounded tracking-widest text-sm" data-testid="exam-timer-display">{mmss}</p>
          </div>
          <div className="flex gap-1"><div className="h-9 w-9 bg-slate-300 rounded" /><div className="h-9 w-9 bg-slate-300 rounded" /></div>
        </div>
      </header>
      {/* SYMBOLS / INSTRUCTIONS */}
      <div className="px-3 py-1 flex gap-4 border-b border-slate-200 shrink-0">
        <button className="text-orange-500 font-bold text-sm underline">SYMBOLS</button>
        <button className="text-orange-500 font-bold text-sm underline">INSTRUCTIONS</button>
      </div>
      {/* ACTION ROW */}
      <div className="px-3 py-2 flex items-center gap-2 shrink-0 flex-wrap">
        <div className="flex gap-1">
          {groups.map((g, i) => {
            const locked = submittedSecs.includes(g.name); const active = i === secIdx;
            return <button key={i} onClick={() => switchSection(i)} disabled={!active} data-testid={`section-tab-${i}`}
              className={`px-3 py-1.5 rounded text-sm font-bold ${active ? "bg-green-600 text-white" : locked ? "bg-slate-200 text-slate-400" : "bg-blue-700 text-white"}`}>PART-{String.fromCharCode(65 + i)}{locked && " ✓"}</button>;
          })}
        </div>
        <div className="flex gap-2 mx-auto">
          <button onClick={markNext} data-testid="mark-for-review-btn" className="bg-blue-600 text-white text-sm font-bold px-3 py-1.5 rounded">Mark for Review</button>
          <button onClick={saveNext} data-testid="save-and-next-btn" className="bg-blue-600 text-white text-sm font-bold px-3 py-1.5 rounded">Save &amp; Next</button>
          <button onClick={() => setReviewSec(true)} data-testid="submit-section-btn" className="bg-blue-600 text-white text-sm font-bold px-3 py-1.5 rounded">Submit Section</button>
          <button onClick={() => setReviewTest(true)} data-testid="submit-test-btn" className="bg-blue-600 text-white text-sm font-bold px-3 py-1.5 rounded">Submit Test</button>
        </div>
        <p className="font-bold text-slate-800 text-sm whitespace-nowrap">Total Questions answered: <span className="text-amber-500 font-black" data-testid="total-answered">{totalAnswered}</span></p>
      </div>
      {/* QUESTION LABEL ROW */}
      <div className="px-3 pb-2 flex items-center shrink-0">
        <p className="font-bold text-slate-800">Question : {idx + 1}</p>
        <p className="ml-auto mr-[25%] text-red-600 font-bold" data-testid="section-timer">Last <span className="text-xl font-black">{Math.floor(secRemaining / 60)}</span> Minutes</p>
      </div>
      {/* BODY */}
      <div className="flex-1 flex overflow-hidden border-t border-slate-200">
        {/* LEFT question */}
        <div className="flex-1 overflow-y-auto">
          <div className="border border-slate-200 m-3 rounded" style={{ zoom }} data-testid="question-zoom-area">
            <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-200">
              <span className="text-sm text-slate-600">Select Language:</span>
              <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="exam-lang-select" className="border border-slate-300 rounded px-2 py-1 text-sm"><option value="en">English</option><option value="hi">Hindi</option></select>
              <button onClick={saveNext} className="bg-blue-500 text-white text-sm font-bold px-3 py-1 rounded">Save</button>
              <button onClick={() => toast.info("Reported to admin")} className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded">Report</button>
            </div>
            <div className="px-4 py-4 border-b border-slate-200">
              <p className="whitespace-pre-wrap text-sm" data-testid="question-text">{q?.question_text || "Question unavailable."}</p>
              {q?.question_image && <img src={q.question_image} alt="" className="mt-3 max-h-60 rounded border" />}
            </div>
            {(q?.options || []).map((o) => (
              <div key={o.key} className={`w-full flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 text-left ${selected.includes(o.key) ? "bg-indigo-50" : ""}`}>
                <button type="button" onClick={() => pick(o.key)} data-testid={`exam-option-radio-${o.key.toLowerCase()}`} aria-label={`Option ${o.key}`} className={`h-4 w-4 rounded-full border-2 shrink-0 cursor-pointer ${selected.includes(o.key) ? "border-indigo-700 bg-indigo-700" : "border-slate-400 hover:border-indigo-500"}`} />
                <span className="text-slate-700 text-sm">{o.text}</span>
                {o.image && <img src={o.image} alt="" className="h-12 ml-auto" />}
              </div>
            ))}
          </div>
        </div>
        {/* RIGHT palette */}
        <div className="w-96 shrink-0 border-l border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-center gap-2 py-3">
            <span className="text-blue-600 text-lg">▶</span>
            <p className="font-bold text-slate-700" data-testid="exam-subject-name">{curGroup.name}</p>
          </div>
          <div className="px-4 grid grid-cols-5 gap-2 overflow-y-auto flex-1 content-start">
            {curGroup.indices.map((oi, i) => (
              <button key={oi} onClick={() => gotoQ(i)} data-testid={`palette-${i + 1}`} className={`h-7 rounded-md text-xs font-bold ${EDU_SC[stateOf(order[oi].question_id)]} ${idx === i ? "ring-2 ring-slate-800 ring-offset-1" : ""}`}>{i + 1}</button>
            ))}
          </div>
          <div className="bg-slate-200 text-center font-bold py-1.5 text-slate-700">PART-{String.fromCharCode(65 + secIdx)} Analysis</div>
          <div className="px-8 py-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-700">Answered</span><span className="text-amber-500 font-black" data-testid="edu-answered">{aStats.answered + aStats.answered_marked}</span></div>
            <div className="flex justify-between"><span className="text-slate-700">Not Answered</span><span className="text-amber-500 font-black" data-testid="edu-not-answered">{aStats.not_answered + aStats.not_visited}</span></div>
          </div>
          <div className="text-center pb-4 px-4">
            {camOn && <video ref={videoRef} autoPlay muted playsInline data-testid="exam-camera-video" className="w-full max-h-40 rounded mb-2 bg-black object-cover" />}
            <button onClick={toggleCamera} data-testid="exam-camera-btn" className="bg-blue-600 text-white font-bold px-4 py-1.5 rounded text-sm">{camOn ? "Hide Camera" : "Show Camera"}</button>
          </div>
        </div>
      </div>
      {reviewSec && <ReviewSection group={curGroup} secStats={secStats} onClose={() => setReviewSec(false)} onSubmit={() => submitSection(false)} />}
      {reviewTest && <ReviewTest groups={groups} secStats={secStats} onClose={() => setReviewTest(false)} onSubmit={finishTest} />}
      {pauseDlg}
    </div>
  );
  const clk = sectional ? secRemaining : remaining;
  const hh = String(Math.floor(clk / 3600)).padStart(2, "0");
  const mm = String(Math.floor((clk % 3600) / 60)).padStart(2, "0");
  const ss = String(clk % 60).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-40 bg-white flex flex-col text-slate-800" data-testid="tcs-exam">
      {/* HEADER */}
      <header className="border-b border-slate-200 px-3 py-1.5 flex items-center gap-3 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-full bg-indigo-800 text-white flex items-center justify-center text-sm font-black">R</span>
          <div><p className="text-[10px] font-extrabold text-indigo-900 leading-none">Rank Test</p><p className="font-bold text-slate-900 text-sm mt-1">{data.test.title}</p></div>
        </div>
        <div className="mx-auto text-center">
          <p className="text-[10px] font-bold text-slate-500">Time Left</p>
          <div className="flex items-end gap-1 justify-center" data-testid="exam-timer-display">
            {[[hh, "HOURS"], [mm, "MINUTES"], [ss, "SECONDS"]].map(([v, l], k) => (
              <React.Fragment key={l}>
                {k > 0 && <span className="font-black text-lg pb-3">:</span>}
                <div className="text-center">
                  <div className="flex gap-0.5">
                    <span className="bg-slate-800 text-white font-black px-1.5 py-0.5 rounded text-sm">{v[0]}</span>
                    <span className="bg-slate-800 text-white font-black px-1.5 py-0.5 rounded text-sm">{v[1]}</span>
                  </div>
                  <span className="text-[8px] text-slate-500 tracking-wide">{l}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-lg">☀</span>
          <ZoomBtns cls="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded" />
          <button onClick={() => setPauseAsk(true)} data-testid="exam-pause-btn" className="border border-slate-300 rounded px-2 py-1 text-sm font-bold">|| Pause</button>
          <button onClick={toggleFs} data-testid="fullscreen-btn" className="border border-slate-300 rounded px-2 py-1 text-sm font-bold">{fsLabel}</button>
        </div>
      </header>
      {/* SECTION TABS */}
      <div className="bg-white border-b border-slate-200 px-4 flex items-center gap-6 overflow-x-auto scroll-hide shrink-0">
        {groups.map((g, i) => {
          const locked = sectional && submittedSecs.includes(g.name); const active = i === secIdx;
          return <button key={i} onClick={() => switchSection(i)} disabled={locked} data-testid={`section-tab-${i}`}
            className={`py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${active ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"} ${locked ? "opacity-40" : ""}`}>{g.name}{locked && " ✓"}</button>;
        })}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* LEFT */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center px-4 py-2 border-b border-slate-200 bg-white">
            <p className="font-bold text-slate-800">No. {idx + 1}</p>
            <div className="ml-auto flex items-center gap-2">
              <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="exam-lang-select" className="border border-slate-300 rounded px-2 py-1.5 text-sm"><option value="en">A / English</option><option value="hi">आ / Hindi</option></select>
              <button onClick={saveNext} className="bg-blue-500 text-white font-bold px-4 py-1.5 rounded text-sm">Save</button>
              <button onClick={() => toast.info("Reported to admin")} className="bg-red-500 text-white font-bold px-4 py-1.5 rounded text-sm">Report</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5" style={{ zoom }} data-testid="question-zoom-area">
            <p className="whitespace-pre-wrap text-slate-800 text-base" data-testid="question-text">{q?.question_text || "Question unavailable."}</p>
            {q?.question_image && <img src={q.question_image} alt="" className="mt-3 max-h-64 rounded border" />}
            <div className="mt-6 space-y-5">{(q?.options || []).map((o) => (
              <button key={o.key} onClick={() => pick(o.key)} data-testid={`exam-option-radio-${o.key.toLowerCase()}`} className="flex items-center gap-3 text-left w-full">
                <span className={`h-5 w-5 rounded-full border-2 shrink-0 ${selected.includes(o.key) ? "border-indigo-700 bg-indigo-700" : "border-slate-400"}`} />
                <span className="text-slate-700">{o.text}</span>
                {o.image && <img src={o.image} alt="" className="h-12 ml-2" />}
              </button>
            ))}</div>
          </div>
          <div className="bg-white border-t border-slate-200 p-3 flex items-center gap-2">
            <button onClick={markNext} data-testid="mark-save-btn" className="px-4 py-2 rounded border border-blue-400 text-blue-600 text-sm font-semibold">Mark &amp; Save</button>
            <button onClick={clearResp} data-testid="clear-response-btn" className="px-4 py-2 rounded border border-red-400 text-red-500 text-sm font-semibold">Clear Response</button>
            <button onClick={saveNext} data-testid="save-and-next-btn" className="ml-auto px-5 py-2 rounded bg-blue-500 text-white text-sm font-bold">Save &amp; Next</button>
          </div>
        </div>
        {/* RIGHT */}
        <div className="hidden lg:flex w-96 shrink-0 bg-sky-100 border-l border-slate-200 flex-col overflow-hidden">
          <div className="p-3 border-b border-sky-200 flex items-center gap-2"><span className="h-6 w-6 rounded-full bg-slate-300" /><span className="font-semibold text-slate-700">nitin</span></div>
          <div className="p-3 flex flex-wrap gap-x-3 gap-y-1 border-b border-sky-200">
            {[["bg-green-600", aStats.answered, "Answered"], ["bg-violet-600", aStats.marked, "Marked"], ["bg-slate-400", aStats.not_visited, "Not Visited"], ["bg-violet-800", aStats.answered_marked, "Marked & Answered"], ["bg-red-500", aStats.not_answered, "Not Answered"]].map(([c, n, t], k) => (
              <div key={k} className="flex items-center gap-1 text-xs"><span className={`h-5 w-5 rounded-full ${c} text-white flex items-center justify-center text-[10px] font-bold`}>{n}</span><span className="text-slate-600">{t}</span></div>
            ))}
          </div>
          <div className="bg-white mx-3 mt-3 px-3 py-2 rounded border border-slate-200 text-sm"><span className="font-bold">Section:</span> <span data-testid="exam-subject-name">{curGroup.name}</span></div>
          <div className="px-3 py-3 grid grid-cols-5 gap-2 overflow-y-auto flex-1 content-start">
            {curGroup.indices.map((oi, i) => (
              <button key={oi} onClick={() => gotoQ(i)} data-testid={`palette-${i + 1}`} className={`h-9 rounded-md text-xs font-bold shadow-sm ${SC[stateOf(order[oi].question_id)]} ${idx === i ? "ring-2 ring-indigo-800 ring-offset-1" : ""}`}>{i + 1}</button>
            ))}
          </div>
          <div className="p-3 space-y-2 bg-sky-100">
            <div className="flex gap-2 justify-center">
              <button className="border border-slate-400 rounded px-3 py-1.5 text-sm font-bold">Question Paper</button>
              <button className="border border-slate-400 rounded px-3 py-1.5 text-sm font-bold">Instructions</button>
            </div>
            {sectional && <button onClick={() => setReviewSec(true)} data-testid="submit-section-btn" className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 rounded text-sm">Submit Section</button>}
            <button onClick={() => setReviewTest(true)} data-testid="submit-test-btn" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 rounded text-sm">Submit Test</button>
          </div>
        </div>
      </div>

      {reviewSec && <ReviewSection group={curGroup} secStats={secStats} onClose={() => setReviewSec(false)} onSubmit={() => submitSection(false)} />}
      {reviewTest && <ReviewTest groups={groups} secStats={secStats} onClose={() => setReviewTest(false)} onSubmit={finishTest} />}
      {pauseDlg}
    </div>
  );
}

function Topbar({ title, timer, lang, setLang }) {
  return (
    <header className="h-14 bg-white border-b flex items-center px-4 gap-3 shrink-0">
      <p className="font-display font-bold text-slate-900 truncate">{title}</p>
      {timer != null && <div className="mx-auto flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg font-black text-lg" data-testid="exam-timer-display"><Clock className="h-4 w-4" /> {timer}</div>}
      {setLang && <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="exam-lang-select" className="ml-auto border rounded-lg px-2 py-1 text-sm"><option value="en">English</option><option value="hi">हिंदी</option></select>}
    </header>
  );
}

function Modal({ title, children }) {
  return <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
    <div className="bg-white rounded-xl w-full max-w-lg p-6" data-testid="exam-modal">
      <h3 className="font-display font-bold text-lg border-b pb-3 mb-4">{title}</h3>{children}
    </div></div>;
}

function InstructionPage({ stage, test, lang, setLang, agree, setAgree, onBack, onNext, onStart }) {
  const first = stage === "instr1";
  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="flex-1 overflow-y-auto p-6 sm:p-10">
        <h1 className="font-display text-2xl font-extrabold text-slate-800 mb-4">{first ? "Instructions, Terms & Conditions" : "General Instructions"}</h1>
        {first ? (
          <div className="text-sm text-slate-700 space-y-2 max-w-4xl" data-testid="instructions-body">
            {test.instructions_pdf && <iframe title="instr" src={`${process.env.REACT_APP_BACKEND_URL}${test.instructions_pdf}`} className="w-full h-[70vh] border rounded" />}
          </div>
        ) : (
          <div className="max-w-3xl">
            <p className="font-semibold text-indigo-800 mb-3">The symbols used in the test are shown below.</p>
            <div className="space-y-2 text-sm text-slate-700">
              <Legend cls="bg-slate-200 text-slate-700" t="Not visited" />
              <Legend cls="bg-red-500 text-white" t="Not answered" />
              <Legend cls="bg-green-600 text-white" t="Answered" />
              <Legend cls="bg-violet-600 text-white" t="Marked for review" />
              <Legend cls="bg-violet-600 text-white ring-2 ring-green-400" t="Answered & marked (will be evaluated)" />
            </div>
            <div className="mt-6">
              <label className="text-sm font-semibold text-slate-700">Select Test Language</label>
              <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="test-language-select" className="ml-3 border rounded-lg px-3 py-1.5 text-sm"><option value="en">English</option><option value="hi">हिंदी / Hindi</option></select>
            </div>
            <label className="flex items-start gap-2 mt-6 text-sm text-slate-700 max-w-3xl cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="agree-checkbox" className="mt-1" />
              <span>I have read all the instructions carefully and have understood them. I agree not to use unfair means in this examination. The decision of Rank Test will be final.</span>
            </label>
          </div>
        )}
      </div>
      <div className="border-t p-4 flex items-center justify-between bg-white">
        <button onClick={onBack} data-testid="instr-back-btn" className="px-6 py-2.5 rounded-lg bg-amber-500 text-white font-bold">{first ? "Go to Tests" : "Back"}</button>
        {first ? <button onClick={onNext} data-testid="instr-next-btn" className="px-8 py-2.5 rounded-lg bg-blue-600 text-white font-bold">Next</button>
          : <button onClick={onStart} disabled={!agree} data-testid="start-test-btn" className="px-8 py-2.5 rounded-lg bg-blue-600 text-white font-bold disabled:opacity-40">Start Test</button>}
      </div>
    </div>
  );
}
const Legend = ({ cls, t }) => <div className="flex items-center gap-3"><span className={`h-7 w-7 rounded ${cls} flex items-center justify-center text-xs font-bold`}>1</span> {t}</div>;

function StatTable({ rows, cols }) {
  return <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="review-table">
    <thead className="text-slate-500 text-xs"><tr>{cols.map((c) => <th key={c} className="text-left p-2 font-semibold">{c}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} className="border-t">{r.map((c, j) => <td key={j} className="p-2 text-slate-700">{c}</td>)}</tr>)}</tbody>
  </table></div>;
}

function ReviewSection({ group, secStats, onClose, onSubmit }) {
  const s = secStats(group);
  return <Modal title="Review section">
    <StatTable cols={["Section name", "Questions", "Answered", "Not Answered", "Marked Answered", "Marked for Review", "Not Visited"]}
      rows={[[group.name, s.count, s.answered, s.not_answered, s.answered_marked, s.marked, s.not_visited]]} />
    <div className="flex justify-end gap-2 mt-4">
      <button onClick={onSubmit} data-testid="confirm-submit-section-btn" className="px-4 py-2 rounded bg-blue-600 text-white font-bold text-sm">Submit Section</button>
      <button onClick={onClose} className="px-4 py-2 rounded border font-semibold text-sm">Cancel</button>
    </div>
  </Modal>;
}

function ReviewTest({ groups, secStats, onClose, onSubmit }) {
  return <Modal title="Review test before submit">
    <StatTable cols={["Section name", "Questions", "Answered", "Not Answered", "Marked Answered", "Marked for Review", "Not Visited"]}
      rows={groups.map((g) => { const s = secStats(g); return [g.name, s.count, s.answered, s.not_answered, s.answered_marked, s.marked, s.not_visited]; })} />
    <div className="flex justify-end gap-2 mt-4">
      <button onClick={onClose} className="px-4 py-2 rounded border font-semibold text-sm">Cancel</button>
      <button onClick={onSubmit} data-testid="confirm-submit-test-btn" className="px-4 py-2 rounded bg-teal-700 text-white font-bold text-sm">Submit Test</button>
    </div>
  </Modal>;
}


function PauseDialog({ onYes, onNo }) {
  return <Modal title="Pause Test">
    <p className="text-slate-600 mb-5" data-testid="pause-dialog-text">Do you want to pause the test?</p>
    <div className="flex justify-end gap-2">
      <button onClick={onNo} data-testid="pause-no-btn" className="px-5 py-2 rounded border font-semibold text-sm">No</button>
      <button onClick={onYes} data-testid="pause-yes-btn" className="px-5 py-2 rounded bg-blue-600 text-white font-bold text-sm">Yes</button>
    </div>
  </Modal>;
}
